import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import PanelCard from '../../panel-card/panel-card';
import AddShipment from '../../add-order-lines/addShipment';
import useDialog from '../../dialog/useDialog';
import AddAddress from '../../add-order-lines/addAddress';
import Button from '../../button/button';
import { NextIcon, PrevIcon } from '../../icons/icons';
import Dialog from '../../dialog/dialog';
import Confirmation from '../../confirmation/confirmation';
import { fetchAddress } from '../../../helpers/utils';
import ContractedBooking from './contracted-booking';
import DialogPanel from '../../dialog-panel/dialog-panel';
import DialogPanelBody from '../../dialog-panel/dialog-panel-body/dialog-panel-body';
import DialogPanelFooter from '../../dialog-panel/dialog-panel-footer/dialog-panel-footer';
import { FETCH_CONTRACTS } from '../../../graphQL/saveBooking';
import GlobalLoader from '../../global-loader/global-loader';
import { giveProfile } from '../../global-notification/notification-list';
import { checkPermission } from '../../../helpers/permissionHelper';

const ShipmentDetailsTab = forwardRef(
  (
    {
      setSupplierTab,
      partiallyBooked,
      bookingValue,
      setBookingVal,
      originValues,
      destinationValues,
      setOriginVal,
      setOriginValues,
      setPickUpValues,
      setDeliveryValues,
      setDestyValues,
      setDestinationValues,
      setTabDetails,
      options,
      setOptions,
      typeOfMove,
      setTypeOfMove,
      setBookingValue,
      setOptionValues,
      disableNxtShip,
      consigneeId,
      contractList,
      setContractList,
      selectedContract,
      setSelectedContract,
      isConsoliatedBooking,
      warehouseList,
      modeSelectionConsole,
      isConsole,
    },
    ref
  ) => {
    const [t] = useTranslation('manageorders');
    const { relation } = useParams();

    const { isShowing: isActivateDislog, toggle: toggleDialog } = useDialog();
    const { isShowing: isContractedDislog, toggle: toggleContractedDialog } =
      useDialog();

    const [refreshForm, setRefreshForm] = useState(true);
    const [callRefreshForm, setCallRefreshForm] = useState(true);
    const [confirmationData, setConfirmationData] = useState([]);
    const [confirmationMessage, setConfirmationMessage] = useState([]);
    const [confirmationField, setConfirmationField] = useState('');
    const [isNextBtnTriggered, setIsNextBtnTriggered] = useState(false);
    const [isContractFetched, setIsContractFetched] = useState(false);

    const isFirstRender = useRef(true);
    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      setRefreshForm(false);
      setTimeout(() => {
        setRefreshForm(true);
      }, [100]);
    }, [callRefreshForm]);

    const { loading, refetch } = useQuery(FETCH_CONTRACTS, {
      variables: {
        input: {
          originAddress: originValues,
          destinationAddress: destinationValues,
          equipmentType: bookingValue?.equipmentType?.value,
          truckType: bookingValue?.truckType?.value,
          profileType: giveProfile(relation),
        },
      },
      skip: !isNextBtnTriggered,
      onCompleted: (data) => {
        setContractList(data?.fetchForwarderContracts?.data);
        setIsContractFetched(true);
      },
    });

    // Contracts should not be visible for RFQ booking
    const allowContractBooking =
      checkPermission(giveProfile(relation), 'Booking', 'contractedBooking') &&
      !consigneeId;

    const handleShipmentSubmit = async () => {
      const isInlandMode = bookingValue?.mode?.value === 'Inland';
      if (!allowContractBooking || !isInlandMode) {
        setTabDetails((prev) => ({
          ...prev,
          shipmentDetails: true,
        }));
        setSupplierTab('confirm entity');
        return;
      }
      setIsNextBtnTriggered(true);
      setIsContractFetched(false);
      await refetch();
    };

    useEffect(() => {
      if (!isNextBtnTriggered || !isContractFetched || loading) return;
      if (allowContractBooking && bookingValue?.mode?.value === 'Inland') {
        if (contractList?.length) {
          toggleContractedDialog();
        } else {
          setTabDetails((prev) => ({
            ...prev,
            shipmentDetails: true,
          }));
          if (giveProfile(relation) !== 'customBroker') {
            setSupplierTab('confirm entity');
          }
          if (giveProfile(relation) === 'customBroker') {
            toast.error(
              t(
                'manageordersscreen.createbookingmenu.contractedbooking.noContractedBooking'
              )
            );
            setTabDetails((prev) => ({
              ...prev,
              disableNxtShip: true,
            }));
          }
        }
      }
      setIsNextBtnTriggered(false);
    }, [loading, isContractFetched]);

    const previousValuesRef = useRef({
      origin: originValues?.origin,
      destination: destinationValues?.destination,
    });
    useEffect(() => {
      // This useEffect should be executed only if the origin and destination values are actually changed and not due to referential equality
      const { origin, destination } = previousValuesRef.current;
      if (
        origin !== originValues?.origin ||
        destination !== destinationValues?.destination
      ) {
        previousValuesRef.current = {
          origin: originValues?.origin,
          destination: destinationValues?.destination,
        };
        if (
          selectedContract?._id &&
          allowContractBooking &&
          bookingValue?.mode?.value === 'Inland'
        ) {
          setContractList([]);
          setSelectedContract({});
        }
      }
    }, [
      JSON.stringify(originValues?.origin),
      JSON.stringify(destinationValues?.destination),
    ]);

    // memoize it
    const warehouseListOptions = warehouseList?.map((obj) => {
      return {
        id: obj?._id,
        label: obj?.warehouseName,
        value: obj?.warehouseName,
      };
    });
    useImperativeHandle(ref, () => ({
      handleShipmentSubmit,
    }));

    return (
      <div>
        {loading && (
          <GlobalLoader newLoad={true} height="6.944vw" width="6.944vw" />
        )}
        {!loading && (
          <div className="supplier-booking-content">
            <div className="add-shipment-wrapper">
              <div className="add-shipment-left">
                <PanelCard
                  cardTitle={t(
                    'manageordersscreen.createbookingmenu.shipmentdetails.title'
                  )}
                  noShadow
                  border
                  disabled={partiallyBooked}
                >
                  {refreshForm && (
                    <AddShipment
                      mode={bookingValue?.mode}
                      containerType={bookingValue?.containerType}
                      cargoLoad={bookingValue?.cargoLoad}
                      typeOfMove={bookingValue?.typeOfMove}
                      equipmentType={bookingValue?.equipmentType}
                      incoTerm={bookingValue?.incoTerm}
                      equipTypeQty={bookingValue?.equipTypeQty}
                      numberOfTrucks={bookingValue?.numberOfTrucks}
                      shipmentFrequency={bookingValue?.shipmentFrequency}
                      totalWeight={bookingValue?.totalWeight}
                      weightUnit={bookingValue?.weightUnit}
                      shipmentType={bookingValue?.shipmentType}
                      airFreType={bookingValue?.airFreightType}
                      setEquipTypeQty={setBookingVal('equipTypeQty')}
                      truckType={bookingValue?.truckType}
                      setTruckType={setBookingVal('truckType')}
                      setMode={(e) => {
                        if (
                          e.value !== '' &&
                          (originValues?.origin !== '' ||
                            bookingValue?.typeOfMove?.value !== undefined ||
                            bookingValue?.containerType?.value ||
                            bookingValue?.cargoLoad?.value) &&
                          !isConsole
                        ) {
                          toggleDialog();
                          setConfirmationData(e);
                          setConfirmationMessage(
                            t(
                              'manageordersscreen.createbookingmenu.shipmentdetails.modeConfirmation'
                            )
                          );
                          setConfirmationField('mode');
                        } else if (
                          e.value !== '' &&
                          originValues?.origin === '' &&
                          (typeOfMove.value === '' ||
                            typeOfMove.value === undefined)
                        ) {
                          setBookingVal('mode')(e);
                        }
                      }}
                      changeMode={(field) => {
                        if (
                          field === 'mode' &&
                          bookingValue?.mode?.value &&
                          (originValues?.origin ||
                            bookingValue?.typeOfMove?.value)
                        ) {
                          toggleDialog();
                          setConfirmationData({
                            label: '',
                            value: '',
                          });
                          setConfirmationMessage(
                            t(
                              'manageordersscreen.createbookingmenu.shipmentdetails.modeConfirmation'
                            )
                          );
                          setConfirmationField(field);
                        } else if (
                          field === 'mode' &&
                          bookingValue?.mode?.value &&
                          (!originValues?.origin || !typeOfMove.value)
                        ) {
                          setBookingVal('mode')({
                            label: '',
                            value: '',
                          });
                        }
                      }}
                      setContainerType={setBookingVal('containerType')}
                      setCargoLoad={setBookingVal('cargoLoad')}
                      setEquipmentType={setBookingVal('equipmentType')}
                      setTypeOfMove={setBookingVal('typeOfMove')}
                      setIncoTerm={setBookingVal('incoTerm')}
                      setNumberOfTrucks={setBookingVal('numberOfTrucks')}
                      setShipmentFrequency={setBookingVal('shipmentFrequency')}
                      setTotalWeight={setBookingVal('totalWeight')}
                      setWeightUnit={setBookingVal('weightUnit')}
                      setShipmentType={setBookingVal('shipmentType')}
                      setAirFreType={setBookingVal('airFreightType')}
                      disableMode={isActivateDislog}
                      consigneeId={consigneeId}
                      isConsoliatedBooking={isConsoliatedBooking}
                      modeSelectionConsole={modeSelectionConsole}
                      isConsole={isConsole}
                    />
                  )}
                </PanelCard>
              </div>
              <div className="add-shipment-right">
                <PanelCard
                  cardTitle={t(
                    'manageordersscreen.createbookingmenu.shipmentdetails.addaddressdetails'
                  )}
                  noShadow
                  border
                  disabled={
                    partiallyBooked ||
                    Object.keys(bookingValue?.mode)?.length === 0 ||
                    Object.keys(bookingValue?.typeOfMove)?.length === 0
                  }
                >
                  <AddAddress
                    originLabel={
                      bookingValue?.mode?.value === 'Inland'
                        ? t(
                            'manageordersscreen.createbookingmenu.shipmentdetails.origindoor'
                          )
                        : t(
                            'manageordersscreen.createbookingmenu.shipmentdetails.originport'
                          )
                    }
                    destinationLabel={
                      bookingValue?.mode?.value === 'Inland'
                        ? t(
                            'manageordersscreen.createbookingmenu.shipmentdetails.destinationdoor'
                          )
                        : t(
                            'manageordersscreen.createbookingmenu.shipmentdetails.destinationport'
                          )
                    }
                    Mode={bookingValue?.mode}
                    TypeOfMove={
                      bookingValue?.mode?.value === 'Inland'
                        ? {
                            label: t(
                              'manageordersscreen.createbookingmenu.shipmentdetails.typeOfMoveOptions.door to door'
                            ),
                            value: 'door to door',
                          }
                        : {
                            label: t(
                              'manageordersscreen.createbookingmenu.shipmentdetails.typeOfMoveOptions.port to port'
                            ),
                            value: 'port to port',
                          }
                    }
                    origin={originValues.origin}
                    destination={destinationValues?.destination}
                    setOrigin={setOriginVal('origin')}
                    setDestination={setDestyValues('destination')}
                    originOptions={options?.originOptions}
                    setOrginOptions={setOptionValues('originOptions')}
                    destinationOptions={options?.destinationOptions}
                    setDestinationOptions={setOptionValues(
                      'destinationOptions'
                    )}
                    originGeoCode={originValues?.originGeoCode}
                    setOriginGeoCode={setOriginVal('originGeoCode')}
                    destinationGeoCode={destinationValues?.destinationGeoCode}
                    setDestinationGeoCode={setDestyValues('destinationGeoCode')}
                    setOriginDoorAddress={(e) => {
                      setOriginVal('originDoorAddress')(e);
                      if (bookingValue?.mode?.value !== 'Inland') {
                        setOriginVal('originCountry')(
                          fetchAddress(
                            'origin',
                            'country',
                            e,
                            destinationValues?.destinationDoorAddress,
                            originValues?.originDoorAddress
                          )
                        );
                      } else {
                        // since we now have new address format for inland
                        setOriginVal('originCountry')(e?.country?.name);
                      }
                    }}
                    disable={
                      partiallyBooked ||
                      Object.keys(bookingValue?.mode)?.length === 0 ||
                      Object.keys(bookingValue?.typeOfMove)?.length === 0
                    }
                    setDestinationDoorAddress={(e) => {
                      setDestyValues('destinationDoorAddress')(e);
                      if (bookingValue?.mode?.value !== 'Inland') {
                        setDestyValues('destinationCountry')(
                          fetchAddress(
                            'destination',
                            'country',
                            e,
                            destinationValues?.destinationDoorAddress,
                            originValues?.originDoorAddress
                          )
                        );
                      } else {
                        // since we now have new address format for inland
                        setDestyValues('destinationCountry')(e?.country?.name);
                      }
                    }}
                    setOriginCountry={setOriginVal('originCountry')}
                    setDestinationCountry={setDestyValues('destinationCountry')}
                    warehouseListOptions={warehouseListOptions}
                    setBookingVal={setBookingVal}
                    originLocation={bookingValue?.originLocation?.label}
                    destinationLocation={
                      bookingValue?.destinationLocation?.label
                    }
                  />
                </PanelCard>
              </div>
            </div>
            <div className="supplier-booking-button-area justify-between">
              <Button
                label={t(
                  'manageordersscreen.createbookingmenu.shipmentdetails.back'
                )}
                styles="back"
                size="large"
                icon={<PrevIcon />}
                spacing="0.694vw"
                className="px-24"
                onClick={() => {
                  if (isConsoliatedBooking) {
                    setSupplierTab('consolidated packaging details');
                  } else if (consigneeId) {
                    setSupplierTab('packaging details');
                  } else if (isConsole) {
                    setSupplierTab('package details console');
                  } else setSupplierTab('booking details');
                }}
              />
              <Button
                label={t(
                  'manageordersscreen.createbookingmenu.shipmentdetails.next'
                )}
                styles="submit"
                size="large"
                disabled={disableNxtShip}
                onClick={handleShipmentSubmit}
                iconRight={<NextIcon />}
                spacing="0.694vw"
                className="px-24"
              />
            </div>
          </div>
        )}

        <Dialog
          isShowing={isActivateDislog}
          hide={toggleDialog}
          placement="center"
        >
          <Confirmation
            cancelButtonText={t(
              'manageordersscreen.createbookingmenu.shipmentdetails.warning.cancel'
            )}
            submitButtonText={t(
              'manageordersscreen.createbookingmenu.shipmentdetails.warning.confirm'
            )}
            title={t(
              'manageordersscreen.createbookingmenu.shipmentdetails.warning.title'
            )}
            description={confirmationMessage}
            onSubmitClick={() => {
              if (confirmationField === 'typeOfMove') {
                setTypeOfMove(confirmationData);
              }
              if (confirmationField === 'mode') {
                setBookingValue({
                  mode: {},
                  containerType: {},
                  cargoLoad: {},
                  equipmentType: {},
                  truckType: {},
                  shipmentFrequency: {},
                  totalWeight: null,
                  equipTypeQty: 1,
                  typeOfMove: {},
                  incoTerm: {},
                });
              }
              setBookingVal('mode')(confirmationData);
              toggleDialog();
              setOptions({
                originOptions: [],
                destinationOptions: [],
                pickUpOptions: [],
                deliveryOptions: [],
              });
              setDestinationValues({
                destinationCountry: '',
                destinationDoorAddress: [],
                destinationGeoCode: [],
                destination: '',
              });
              setDeliveryValues({
                destinationCountry: '',
                destinationDoorAddress: [],
                destinationGeoCode: [],
                destination: '',
              });
              setOriginValues({
                origin: '',
                originGeoCode: [],
                originDoorAddress: [],
                originCountry: '',
              });
              setPickUpValues({
                origin: '',
                originGeoCode: [],
                originDoorAddress: [],
                originCountry: '',
              });
              setCallRefreshForm((prev) => !prev);
            }}
            onCancelClick={() => {
              toggleDialog();
              setCallRefreshForm((prev) => !prev);
            }}
          />
        </Dialog>
        <Dialog
          isShowing={isContractedDislog}
          hide={toggleContractedDialog}
          placement="center"
          maskZindex={10000}
        >
          <DialogPanel
            title={t(
              'manageordersscreen.createbookingmenu.contractedbooking.title'
            )}
          >
            <DialogPanelBody>
              <ContractedBooking
                contractList={contractList}
                selectedContract={selectedContract}
                setSelectedContract={setSelectedContract}
              />
            </DialogPanelBody>
            <DialogPanelFooter>
              {giveProfile(relation) !== 'customBroker' && (
                <Button
                  label={t(
                    'manageordersscreen.createbookingmenu.contractedbooking.createspotrate'
                  )}
                  color="cancel"
                  radius="1.25vw"
                  size="small"
                  onClick={() => {
                    setSelectedContract({});
                    setContractList([]);
                    setTabDetails((prev) => ({
                      ...prev,
                      shipmentDetails: true,
                    }));
                    setSupplierTab('confirm entity');
                  }}
                />
              )}
              <Button
                type="submit"
                label={t(
                  'manageordersscreen.createbookingmenu.contractedbooking.selectproceed'
                )}
                color="submit"
                radius="1.25vw"
                size="small"
                onClick={() => {
                  setTabDetails((prev) => ({
                    ...prev,
                    shipmentDetails: true,
                  }));
                  setSupplierTab('confirm entity');
                  toast.success(
                    t(
                      'manageordersscreen.createbookingmenu.contractedbooking.contractedRateSelected'
                    )
                  );
                }}
                disabled={!selectedContract?._id}
              />
            </DialogPanelFooter>
          </DialogPanel>
        </Dialog>
      </div>
    );
  }
);

export default ShipmentDetailsTab;
ShipmentDetailsTab.propTypes = {
  setSupplierTab: PropTypes.func,
  partiallyBooked: PropTypes.bool,
  bookingValue: PropTypes.instanceOf(Object),
  setBookingVal: PropTypes.func,
  originValues: PropTypes.instanceOf(Object),
  destinationValues: PropTypes.instanceOf(Object),
  setOriginVal: PropTypes.func,
  setOriginValues: PropTypes.func,
  setDestinationValues: PropTypes.func,
  setPickUpValues: PropTypes.func,
  setDeliveryValues: PropTypes.func,
  setDestyValues: PropTypes.func,
  typeOfMove: PropTypes.instanceOf(Object),
  setTypeOfMove: PropTypes.func,
  setTabDetails: PropTypes.func,
  options: PropTypes.instanceOf(Object),
  setOptions: PropTypes.instanceOf(Object),
  setBookingValue: PropTypes.func,
  setOptionValues: PropTypes.func,
  disableNxtShip: PropTypes.bool,
  consigneeId: PropTypes.number,
  contractList: PropTypes.instanceOf(Array),
  setContractList: PropTypes.bool,
  selectedContract: PropTypes.instanceOf(Object),
  setSelectedContract: PropTypes.func,
  isConsoliatedBooking: PropTypes.bool,
  warehouseList: PropTypes.instanceOf(Array),
  modeSelectionConsole: PropTypes.string,
  isConsole: PropTypes.bool,
};
ShipmentDetailsTab.defaultProps = {
  setSupplierTab: () => {},
  partiallyBooked: false,
  bookingValue: {},
  setBookingVal: () => {},
  originValues: {},
  destinationValues: {},
  setOriginVal: () => {},
  setOriginValues: () => {},
  setDestinationValues: () => {},
  setPickUpValues: () => {},
  setDeliveryValues: () => {},
  setDestyValues: () => {},
  typeOfMove: {},
  setTypeOfMove: () => {},
  setTabDetails: () => {},
  options: {},
  setOptions: () => {},
  setBookingValue: () => {},
  setOptionValues: () => {},
  disableNxtShip: true,
  consigneeId: null,
  contractList: [],
  setContractList: () => {},
  selectedContract: {},
  setSelectedContract: () => {},
  isConsoliatedBooking: false,
  warehouseList: [],
  modeSelectionConsole: '',
  isConsole: false,
};
